import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  FingerPrintIcon,
  CpuChipIcon,
  PlusIcon,
  CheckCircleIcon,
  SignalIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export default function BiometricPage() {
  const [devices, setDevices] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [punchMessage, setPunchMessage] = useState('');
  const [testResults, setTestResults] = useState({});
  const [testingId, setTestingId] = useState(null);

  const [formData, setFormData] = useState({
    device_name: '',
    device_ip: '',
    device_port: 4370,
    serial_number: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dRes, mRes] = await Promise.all([
        api.get('/api/biometric/config'),
        api.get('/api/members')
      ]);
      setDevices(dRes.data.devices || []);
      setMembers(mRes.data.members || []);
      if (mRes.data.members?.length > 0) {
        setSelectedMemberId(mRes.data.members[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingDevice(null);
    setFormData({
      device_name: 'Main Gate ZK-Teco Biometric Reader',
      device_ip: '192.168.1.201',
      device_port: 4370,
      serial_number: 'ZK-BIO-998822',
      is_active: true
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (device) => {
    setEditingDevice(device);
    setFormData({
      device_name: device.device_name || '',
      device_ip: device.device_ip || '',
      device_port: device.device_port || 4370,
      serial_number: device.serial_number || '',
      is_active: device.is_active ?? true
    });
    setShowModal(true);
  };

  const handleSaveDevice = async (e) => {
    e.preventDefault();
    try {
      if (editingDevice) {
        await api.put(`/api/biometric/config/${editingDevice.id}`, formData);
      } else {
        await api.post('/api/biometric/config', formData);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save device configuration');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this biometric device configuration?')) return;
    try {
      await api.delete(`/api/biometric/config/${deviceId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete device configuration');
    }
  };

  const handleTestConnection = async (deviceId) => {
    try {
      setTestingId(deviceId);
      const res = await api.post(`/api/biometric/test-connection/${deviceId}`);
      setTestResults(prev => ({
        ...prev,
        [deviceId]: {
          status: res.data.status,
          message: res.data.message
        }
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [deviceId]: {
          status: 'offline',
          message: err.response?.data?.error || 'Connection ping failed'
        }
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleSimulatePunch = async () => {
    if (!selectedMemberId) {
      alert('Please select a member to simulate punch');
      return;
    }
    try {
      const res = await api.post('/api/biometric/sync-punch', { member_id: selectedMemberId });
      setPunchMessage(res.data.message);
      setTimeout(() => setPunchMessage(''), 5000);
    } catch (err) {
      alert('Biometric punch failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
            <FingerPrintIcon className="w-8 h-8 text-orange-600" />
            Biometric Hardware Integration
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Connect fingerprint scanners, RFID readers, and ZK-Teco biometric devices for automated attendance check-ins.
          </p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-sm transition-all self-start md:self-auto"
        >
          <PlusIcon className="w-5 h-5" /> Add Biometric Device
        </button>
      </div>

      {/* Test Simulation Panel */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white p-6 rounded-2xl shadow-md space-y-3">
        <div className="flex items-center gap-2">
          <CpuChipIcon className="w-6 h-6 text-white" />
          <h2 className="text-lg font-bold">Biometric Live Punch Tester</h2>
        </div>
        <p className="text-xs text-orange-100">
          Simulate a physical fingerprint punch event from the connected hardware device:
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
          <select
            value={selectedMemberId}
            onChange={(e) => setSelectedMemberId(e.target.value)}
            className="w-full sm:w-80 text-sm p-2.5 rounded-xl text-gray-900 outline-none font-medium"
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.first_name} {m.last_name} ({m.phone})
              </option>
            ))}
          </select>
          <button
            onClick={handleSimulatePunch}
            className="w-full sm:w-auto px-6 py-2.5 bg-white text-orange-700 font-extrabold text-sm rounded-xl hover:bg-orange-50 shadow-md transition-all flex items-center justify-center gap-2"
          >
            <FingerPrintIcon className="w-5 h-5 text-orange-600" /> Simulate Fingerprint Punch
          </button>
        </div>

        {punchMessage && (
          <div className="bg-white/20 backdrop-blur-md text-white p-3 rounded-xl text-xs font-bold flex items-center gap-2 border border-white/30">
            <CheckCircleIcon className="w-5 h-5 text-green-300" />
            {punchMessage}
          </div>
        )}
      </div>

      {/* Devices List */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <SignalIcon className="w-5 h-5 text-green-600" /> Connected Biometric Hardware Readers
        </h2>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading biometric device configurations...</div>
        ) : devices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No biometric devices configured. Click "Add Biometric Device" above.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {devices.map((device) => {
              const testInfo = testResults[device.id];
              return (
                <div key={device.id} className="border border-gray-200 p-4 rounded-xl bg-gray-50 flex flex-col justify-between space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-gray-900 text-base">{device.device_name}</h3>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">
                        IP: {device.device_ip}:{device.device_port} • SN: {device.serial_number}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                          device.is_active ? 'text-green-700 bg-green-100' : 'text-gray-600 bg-gray-200'
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${device.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                          {device.is_active ? 'Device Active' : 'Disabled'}
                        </span>
                        {testInfo && (
                          <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                            testInfo.status === 'online' ? 'text-blue-700 bg-blue-100' : 'text-red-700 bg-red-100'
                          }`}>
                            {testInfo.status === 'online' ? '🟢 Ping Online' : '🔴 Ping Offline'}
                          </span>
                        )}
                      </div>
                    </div>
                    <FingerPrintIcon className="w-9 h-9 text-orange-500/80 shrink-0" />
                  </div>

                  {testInfo?.message && (
                    <div className={`p-2.5 rounded-lg text-xs font-medium ${
                      testInfo.status === 'online' ? 'bg-blue-50 text-blue-800 border border-blue-200' : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                      {testInfo.message}
                    </div>
                  )}

                  {/* Actions Bar */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <button
                      onClick={() => handleTestConnection(device.id)}
                      disabled={testingId === device.id}
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 py-1 px-2.5 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <ArrowPathIcon className={`w-3.5 h-3.5 ${testingId === device.id ? 'animate-spin' : ''}`} />
                      {testingId === device.id ? 'Testing...' : 'Test Connection'}
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEditModal(device)}
                        className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Edit Device Config"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDevice(device.id)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Device Config"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 max-w-md w-full shadow-xl relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingDevice ? 'Edit Biometric Device Config' : 'Add Biometric Reader Device'}
            </h2>
            <form onSubmit={handleSaveDevice} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Device Name</label>
                <input
                  type="text"
                  required
                  value={formData.device_name}
                  onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl text-gray-900"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">IP Address</label>
                  <input
                    type="text"
                    required
                    value={formData.device_ip}
                    onChange={(e) => setFormData({ ...formData, device_ip: e.target.value })}
                    className="w-full text-sm border p-2.5 rounded-xl font-mono text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Port</label>
                  <input
                    type="number"
                    required
                    value={formData.device_port}
                    onChange={(e) => setFormData({ ...formData, device_port: e.target.value })}
                    className="w-full text-sm border p-2.5 rounded-xl font-mono text-gray-900"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Serial Number</label>
                <input
                  type="text"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  className="w-full text-sm border p-2.5 rounded-xl font-mono text-gray-900"
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <label htmlFor="is_active" className="text-xs font-bold text-gray-700">Device Active</label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700 shadow-sm"
                >
                  {editingDevice ? 'Update Device' : 'Save Device'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
